import type { Choice } from './types'
import type { ChangeEventHandler } from 'react'

import { useCallback, useState } from 'react'

import { Div, H2, Span } from 'components/common/atoms'
import { CheckBoxItem } from 'components/common/molecules/checkbox-item'
import { ResponsiveCarousel } from 'components/common/molecules/responsive-carousel'
import { COLOR, TEXT_REGULAR } from 'styles/constants'

import { getCheckedState } from './helper'

type Props = IElement & {
  choices: Choice[]
  limit: number
  onChange: ChangeEventHandler<HTMLInputElement>
  label: string
  required?: boolean
  values?: unknown[]
  ascent?: string
  error?: boolean
}

export function LimitedCheckBoxGroup({
  choices,
  limit,
  values = [],
  onChange,
  label,
  ascent,
  required = false,
  ...style
}: Props) {
  const [checkCount, setCheckCount] = useState(0)
  const selectingLimitHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.target.checked) {
        if (checkCount < limit) {
          setCheckCount(checkCount + 1)
          onChange(e)
        }
      } else {
        setCheckCount(checkCount - 1)
        onChange(e)
      }
    },
    [checkCount, limit, onChange],
  )
  return (
    <Div {...style}>
      <H2
        $size="16px"
        $mSize="16px"
        $mWeight={TEXT_REGULAR}
        $weight={TEXT_REGULAR}
        $mLineHeight="28px"
        $lineHeight="28px"
        $color={COLOR.GREY_LV1}
        $margin="0 0 16px 0"
      >
        {label} {ascent && <Span $color={COLOR.GRAY_SCALE_40}>{ascent}</Span>}
        {required && <Span $color={COLOR.RED_LV2}> *</Span>}
      </H2>
      <ResponsiveCarousel itemWidth={88} gapPerItems={8} slidesToScroll={1}>
        {choices.map((choice) => (
          <CheckBoxItem
            key={choice.id}
            id={choice.id}
            title={choice.title}
            subtitle={choice.subtitle}
            name={choice.name}
            value={choice.value}
            checked={values.length <= limit && values.includes(choice.value)}
            onChange={selectingLimitHandler}
            disabled={checkCount === limit && !getCheckedState(choice.id)}
          />
        ))}
      </ResponsiveCarousel>
    </Div>
  )
}
